import { useEffect, useMemo, useRef, useState } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import styles from "./Answer.module.css";
import { Container, Row, Col } from "react-bootstrap"
import 'bootstrap/dist/css/bootstrap.css';

import { AskResponse, getCitationFilePath } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { ThumbDislike20Filled, ThumbDislike20Regular, ThumbLike20Filled, ThumbLike20Regular } from "@fluentui/react-icons";
import { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { useConst } from '@fluentui/react-hooks';
import { animated, useSpring } from "@react-spring/web";

import logo from "../../../public/Ask-Jai-Icon.svg";
import { FaRegThumbsDown, FaRegThumbsUp, FaThumbsUp, FaThumbsDown } from "react-icons/fa";
import { MdSettingsSuggest } from "react-icons/md";


// const facts = [
//     "Do you know Jindal Panther is India’s 1st and largest producer of FE550D TMT Rebar",
//     "Do you know Jindal Panther TMT rebars are produced at our own Integrated Steel Plant.",
//     "Jindal Panther is proud to be a partner in India’s growth journey",
//     "Asly Sariye ki pehchan Jindal Panther ka Nishan",
//     "Desh ki Shaan har Nirman ki Jan, Jindal Panther",
//     "Do you know Jindal Panther is a top choice for Contractors, Architects and Consumers"
// ];

const facts = [
    "Loading"
]

const getRandomFact = () => {
    const randomIndex = Math.floor(Math.random() * facts.length);
    return facts[randomIndex];
};


interface Props {
    answer: AskResponse;
    isSelected?: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onLikeDislikeClicked: (val: number) => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    onFollowupsShown?: () => void; // Callback when follow-ups appear
    onRetryClicked?: () => void;
}

export const Answer = ({
    answer,
    isSelected,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    onLikeDislikeClicked,
    showFollowupQuestions,
    onFollowupsShown,
    onRetryClicked
}: Props) => {
    const selectedCitation = useRef<any>(null);
    // const [selectedCitation, setSelectedCitation] = useState<any>(null);

    const parsedAnswer = useMemo(() => parseAnswerToHtml(answer.answer, onCitationClicked), [answer]);

    // Combine follow-up questions from API and parsed answer
    // Prioritize API questions, then add any parsed ones that aren't duplicates
    const combinedFollowupQuestions = useMemo(() => {
        const apiQuestions = answer.follow_up_questions || answer.follow_up || [];
        const parsedQuestions = parsedAnswer.followupQuestions || [];

        // Use API questions if available, otherwise fall back to parsed
        return apiQuestions.length > 0 ? apiQuestions : parsedQuestions;
    }, [answer.follow_up_questions, answer.follow_up, parsedAnswer.followupQuestions]);

    const menuProps = useConst<IContextualMenuProps>(() => ({
        shouldFocusOnMount: true,
        shouldFocusOnContainer: true,
        items: [
            {
                key: 'Open Page', text: 'Open Page', onClick: () => {
                    if (selectedCitation.current) {
                        const page = getCitationFilePath(selectedCitation.current[0])
                        onCitationClicked(page)
                    }
                }
            },
            {
                key: 'Open File', text: 'Open File', onClick: () => {
                    if (selectedCitation.current) {
                        window.open(selectedCitation.current[1], "__blank")
                    }
                }
            },
        ],
    }));
    // const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    let sanitizedAnswerHtml;

    if (parsedAnswer.answerHtml.includes('https://')) {
        sanitizedAnswerHtml = parsedAnswer.answerHtml;
    } else {
        sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);
    }

    const citationClick = () => {
        console.log("citationClick")
    }

    const [currentFact, setCurrentFact] = useState(getRandomFact());

    useEffect(() => {
        const factInterval = setInterval(() => {
            setCurrentFact(getRandomFact());
        }, 4000); // Update fact every 2 seconds

        return () => clearInterval(factInterval); // Cleanup interval on component unmount
    }, []);


    const formatDate = (date: Date) => {
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: 'Asia/Kolkata',
        };
        return new Intl.DateTimeFormat('en-IN', options).format(date);
    };

    const [receivedTime, setReceivedTime] = useState<Date>(new Date());

    // State to control when to show follow-up questions (after typing animation)
    const [showFollowups, setShowFollowups] = useState<boolean>(false);
    const hasShownFollowupsRef = useRef<boolean>(false);

    useEffect(() => {
        // If followups have already been shown for this answer, keep them visible
        if (hasShownFollowupsRef.current) {
            setShowFollowups(true);
            return;
        }

        // Calculate timing based on content type
        let displayDuration: number;

        setReceivedTime((answer.response_time ? answer.response_time : new Date()));

        if (parsedAnswer.isMarkdown) {
            // For markdown, show follow-ups immediately since there's no typing animation
            displayDuration = 500; // Short delay for markdown content
        } else {
            // Calculate typing animation duration based on message length
            const messageLength = answer.answer.length;
            displayDuration = messageLength * 10; // 10ms per character
        }

        // Show follow-up questions after content is displayed
        const timer = setTimeout(() => {
            setShowFollowups(true);
            hasShownFollowupsRef.current = true;
            // Notify parent that follow-ups are now visible
            if (onFollowupsShown) {
                onFollowupsShown();
            }
        }, displayDuration + 200); // Add 200ms buffer

        return () => clearTimeout(timer);
    }, [answer.answer, answer.response_time, parsedAnswer.isMarkdown, onFollowupsShown]);



    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">

            <div className={styles.classFier}>

                <div className={styles.logojaiicon}>
                    <img src={logo} alt="Ask JAI" style={{ width: '25px' }} />
                </div>
                <Stack.Item grow>
                    {sanitizedAnswerHtml ? (
                        <div className={styles.classFier2}>

                            <div className={styles.answerText}>
                                {parsedAnswer.isMarkdown ? (
                                    // Render markdown content
                                    <div className={styles.fadeIn}>
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                /* -------- HEADINGS (LIKE PARAGRAPH) -------- */
                                                h1: ({ children }) => (
                                                    <p
                                                        style={{
                                                            margin: '12px 0',
                                                            fontWeight: 600,
                                                            fontSize: '13px',
                                                            lineHeight: '18px',
                                                            color: '#000000',
                                                        }}
                                                    >
                                                        {children}
                                                    </p>
                                                ),

                                                h2: ({ children }) => (
                                                    <p
                                                        style={{
                                                            margin: '12px 0',
                                                            fontWeight: 600,
                                                            fontSize: '13px',
                                                            color: '#F37A1F',
                                                            lineHeight: '18px',
                                                        }}
                                                    >
                                                        {children}
                                                    </p>
                                                ),

                                                h3: ({ children }) => (
                                                    <p
                                                        style={{
                                                            margin: '12px 0',
                                                            fontWeight: 600,
                                                            fontSize: '13px',
                                                            color: '#000000',
                                                            lineHeight: '18px',
                                                        }}
                                                    >
                                                        {children}
                                                    </p>
                                                ),

                                                h4: ({ children }) => (
                                                    <p
                                                        style={{
                                                            margin: '12px 0',
                                                            fontWeight: 600,
                                                            fontSize: '13px',
                                                            color: '#000000',
                                                            lineHeight: '18px',
                                                        }}
                                                    >
                                                        {children}
                                                    </p>
                                                ),

                                                /* -------- PARAGRAPH -------- */
                                                p: ({ children }) => (
                                                    <p
                                                        style={{
                                                            margin: '8px 0',
                                                            lineHeight: '18px',
                                                            fontSize: '13px',
                                                            color: '#000000',
                                                        }}
                                                    >
                                                        {children}
                                                    </p>
                                                ),

                                                /* -------- LISTS -------- */
                                                ul: ({ children }) => (
                                                    <ul style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                                                        {children}
                                                    </ul>
                                                ),

                                                ol: ({ children }) => (
                                                    <ol style={{ margin: '8px 0', paddingLeft: '20px', listStyleType: 'decimal' }}>
                                                        {children}
                                                    </ol>
                                                ),

                                                li: ({ children }) => (
                                                    <li
                                                        style={{
                                                            marginBottom: '4px',
                                                            lineHeight: '18px',
                                                            fontSize: '13px',
                                                            color: '#000000',
                                                        }}
                                                    >
                                                        {children}
                                                    </li>
                                                ),

                                                /* -------- INLINE -------- */
                                                strong: ({ children }) => (
                                                    <strong
                                                        style={{
                                                            fontWeight: 600,
                                                            color: '#000000',
                                                        }}
                                                    >
                                                        {children}
                                                    </strong>
                                                ),

                                                em: ({ children }) => (
                                                    <em style={{ fontStyle: 'italic', color: '#000000' }}>
                                                        {children}
                                                    </em>
                                                ),

                                                br: () => <br />,

                                                /* -------- CODE -------- */
                                                code: ({ children }) => (
                                                    <code
                                                        style={{
                                                            backgroundColor: '#f1f3f4',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            fontFamily: 'Consolas, monospace',
                                                            fontSize: '13px',
                                                            color: '#d73a49',
                                                        }}
                                                    >
                                                        {children}
                                                    </code>
                                                ),

                                                pre: ({ children }) => (
                                                    <pre
                                                        style={{
                                                            backgroundColor: '#f1f3f4',
                                                            padding: '12px',
                                                            borderRadius: '6px',
                                                            overflowX: 'auto',
                                                            margin: '8px 0',
                                                            lineHeight: '1.4',
                                                            fontSize: '13px',
                                                        }}
                                                    >
                                                        {children}
                                                    </pre>
                                                ),
                                            }}
                                        >
                                            {parsedAnswer.answerHtml?.replace(/\\n/g, '\n')}
                                        </ReactMarkdown>

                                    </div>
                                ) : (
                                    // Original typing animation for non-markdown content
                                    <div className={styles.fadeIn}>
                                        {answer.answer.split('').map((char, index) => (
                                            <span
                                                key={index}
                                                className={styles.typingAnimationWord}
                                                style={{ animationDelay: `${index * 0.01}s` }}
                                            >
                                                {char}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* -------- FOLLOW-UP QUERY -------- */}
                                {/* {parsedAnswer.followupQuestions?.length > 0 && (
                                    <div className={styles.followupQuestionsContainer}>
                                        {parsedAnswer.followupQuestions.map((question, index) => (
                                            <div className={styles.followupQuestionsTitle}>
                                                {question}
                                                
                                            </div>
                                            
                                        ))}
                                    </div>
                                )} */}

                                {showFollowups &&
                                    !!combinedFollowupQuestions.length &&
                                    showFollowupQuestions && (
                                        <div className={styles.followupQuestionsContainer}>


                                            <div className={styles.followupQuestionsList}>
                                                {combinedFollowupQuestions.slice(0, 1).map((x, i) => (
                                                    <div key={i}>
                                                        {x}
                                                    </div>

                                                ))}
                                            </div>
                                        </div>
                                    )}





                                {/* -------- FOOTER -------- */}

                                <div className={styles.answerFooter}>
                                    {answer.error ? (
                                        <button
                                            className={styles.retryButton}
                                            onClick={onRetryClicked}
                                        >
                                            Retry
                                        </button>
                                    ) : (
                                        /* Like/Dislike Feedback Buttons */
                                        <div className={styles.feedbackButtons}>
                                            {answer.isLike === 1 ? (
                                                <FaThumbsUp
                                                    title="Like this answer"
                                                    aria-label="Like"
                                                    onClick={() => onLikeDislikeClicked(1)}
                                                    className={styles.feedbackActive}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ) : (
                                                <FaRegThumbsUp
                                                    title="Like this answer"
                                                    aria-label="Like"
                                                    onClick={() => onLikeDislikeClicked(1)}
                                                    className={styles.feedbackButton}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            )}

                                            {answer.isLike === -1 ? (
                                                <FaThumbsDown
                                                    title="Dislike this answer"
                                                    aria-label="Dislike"
                                                    onClick={() => onLikeDislikeClicked(-1)}
                                                    className={styles.feedbackActive}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            ) : (
                                                <FaRegThumbsDown
                                                    title="Dislike this answer"
                                                    aria-label="Dislike"
                                                    onClick={() => onLikeDislikeClicked(-1)}
                                                    className={styles.feedbackButton}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                            )}
                                        </div>
                                    )}
                                    <div className={styles.UserTextTimeStamp}>
                                        {formatDate(receivedTime)}
                                    </div>
                                </div>
                            </div>


                            {/* <div className={styles.citationMan}>
                                {!!parsedAnswer.citations.length && (
                                    <Stack.Item>
                                        <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                                            <span className={styles.citationLearnMore}>Citations:</span>

                                            {parsedAnswer.citations.map((x, i) => {

                                                return (

                                                    <a key={i} className={styles.citation} title={x} onClick={() => onCitationClicked(x)}>
                                                        {`${++i}. ${x}`}
                                                    </a>
                                                );
                                            })}

                                        </Stack>
                                    </Stack.Item>
                                )
                                }
                            </div> */}


                            {/* Follow-up Questions - Inside Answer Box */}
                            {/* {showFollowups && !!combinedFollowupQuestions.length && showFollowupQuestions && onFollowupQuestionClicked && (
                                <div className={styles.followupQuestionsContainer}>
                                    <div className={styles.followupQuestionsTitle}><MdSettingsSuggest color="#ffd064" /> You might also want to know:</div>
                                    <div className={styles.followupQuestionsList}>
                                        {combinedFollowupQuestions.map((x, i) => (
                                            <button
                                                key={i}
                                                className={styles.followupQuestionButton}
                                                onClick={() => onFollowupQuestionClicked(x)}
                                                title={x}
                                            >
                                                {x}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )} */}
                        </div>
                    ) : (
                        <Stack.Item grow>
                            <div className={styles.fadeIn}>
                                <div className={styles.loadingCard}>
                                    <span className={styles.loadingText}>{currentFact}</span>
                                    <span className={styles.loadingDots}>
                                        <span className={styles.loadingDot} />
                                        <span className={styles.loadingDot} />
                                        <span className={styles.loadingDot} />
                                    </span>
                                </div>
                            </div>
                        </Stack.Item>
                    )}


                </Stack.Item>
            </div>

        </Stack>
    );
};
